import type { KeyboardEvent } from 'react';

/**
 * Roving-focus keyboard handler for a WAI-ARIA tablist. Ported from the
 * creator-portal prototype (ROU-1192). Arrow/Home/End move the active tab and
 * shift DOM focus to the newly active `[role="tab"]`.
 */
export const handleTabKeyDown = <TValue extends string>(
    event: KeyboardEvent<HTMLElement>,
    values: TValue[],
    activeValue: TValue,
    onChange: (value: TValue) => void
): void => {
    const supportedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!supportedKeys.includes(event.key)) {
        return;
    }

    event.preventDefault();
    const activeIndex = Math.max(values.indexOf(activeValue), 0);
    let nextIndex = activeIndex;

    if (event.key === 'Home') {
        nextIndex = 0;
    }
    if (event.key === 'End') {
        nextIndex = values.length - 1;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (activeIndex - 1 + values.length) % values.length;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (activeIndex + 1) % values.length;
    }

    const tabList = event.currentTarget.closest('[role="tablist"]');
    onChange(values[nextIndex]);
    window.requestAnimationFrame(() => {
        const tabs = tabList?.querySelectorAll<HTMLElement>('[role="tab"]');
        tabs?.[nextIndex]?.focus();
    });
};
